import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Singleton pattern to prevent multiple client instances
let supabaseInstance: ReturnType<typeof createClient<Database>> | null = null;
let tokenProvider: (() => Promise<string | null>) | null = null;

// Hard-coded publishable values for the connected Supabase project. These are
// safe to ship in client code (RLS enforces all access). We read Vite env first
// so local overrides still win, then fall back to the baked-in values so the
// client can never construct with an empty key (which would throw
// "supabaseKey is required" at module load).
const HARDCODED_SUPABASE_URL = "https://fyrvespwoifgpkmjnnhs.supabase.co";
const HARDCODED_SUPABASE_PUBLISHABLE_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ5cnZlc3B3b2lmZ3BrbWpubmhzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTU5NDE1NjcsImV4cCI6MjA3MTUxNzU2N30.axgDTy_SNZqRVTE2CcnSEiunyq5VlC_zVRLcOIEpX10";

const viteEnv =
  (typeof import.meta !== 'undefined' && (import.meta as any).env) || {};

const SUPABASE_URL: string =
  viteEnv.VITE_SUPABASE_URL || HARDCODED_SUPABASE_URL;

const SUPABASE_PUBLISHABLE_KEY: string =
  viteEnv.VITE_SUPABASE_PUBLISHABLE_KEY ||
  viteEnv.VITE_SUPABASE_ANON_KEY ||
  HARDCODED_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  // Should be impossible given the hard-coded fallbacks above, but log so we
  // never silently construct an unusable client.
  console.warn(
    '[supabase] Missing SUPABASE_URL or ANON_KEY',
    { hasUrl: !!SUPABASE_URL, hasKey: !!SUPABASE_PUBLISHABLE_KEY },
  );
}

const resolveAccessToken = async () => {
  if (!tokenProvider) return '';
  try {
    return (await tokenProvider()) || '';
  } catch (error) {
    console.warn('[supabase] Failed to resolve access token', error);
    return '';
  }
};

function createSupabaseClient() {
  if (!supabaseInstance) {
    supabaseInstance = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      auth: {
        storage: typeof localStorage !== 'undefined' ? localStorage : undefined,
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
      },
      global: {
        headers: {
          'X-Client-Info': 'padel-app@1.0.0',
        },
      },
      accessToken: resolveAccessToken,
    });

    console.log('[supabase] Client instance created');
  }

  return supabaseInstance;
}

export const supabase = createSupabaseClient();

export function setSupabaseTokenProvider(provider: (() => Promise<string | null>) | null) {
  tokenProvider = provider;
}

export function createSupabaseClientWithSession(getToken: (() => Promise<string | null>) | null) {
  setSupabaseTokenProvider(getToken);
  return supabase;
}

export function hasSupabaseAuth() {
  return tokenProvider !== null;
}
